import { errorCopy } from '@/api/errorCopy';
/**
 * The account page (TMMAPP-0008, design §14.4 row 3; the v2 page, TMMAPP-0151 §E, prototype states
 * 31–34, P:488–524) at `/me/accounts/:accountId`. The header: "← My Accounts", the `h1`, **Ask about
 * this** (this account), and a chip row — "Institution ···· last4 · Type ·
 * Subtype · CUR · last import {date} · {profile}" (no permission pill, TMMAPP-0161 item 3). Then the tiles (Bank balance, In envelopes,
 * Unallocated; a card's Statement balance, Published to envelopes, Unallocated), then the tabs in v2
 * tab style (TMMAPP-0159 §F 29): **Items to review** (this account's review queue and Tidy up your
 * history), **Bank register**, then Recurring, Cash flow, Schedule, Rules, Permissions and Imports
 * (`?tab=`; `#tidy-up` opens Items to review). Bank-side amounts render in the account's currency;
 * allocations and remainders in the operating currency (MM-D-63) — the prototype's screen 33 converts
 * them, which the data rule doesn't allow — and a foreign account shows its operating equivalent
 * under the bank balance ("≈ {operating}"). Allocation writes go through `allocationsApi` with a
 * per-attempt Idempotency-Key; the SPA refetches after a write rather than parsing the write response.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { destructiveItemSx } from '@/shell/objectMenu';
import { Navigate, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNotify } from '@/shell/NotifyProvider';
import {
  accountsApi,
  institutionLabel,
  openingIsConfirmed,
  type Account,
  type BalanceCheck,
} from '@/api/accounts';
import { exportsApi } from '@/api/exports';
import { ErrorCodes, isApiError, isNotFound, lineBeforeOpening } from '@/api/errors';
import type { OwnerRegisterLine } from '@/api/transactions';
import { ConfirmButton } from '@/components/ConfirmButton';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { statusLabel } from '@/components/statusLabel';
import { parseRouteId } from '@/features/virtualAccount/routeId';
import { BudgetStartLine } from '@/features/accounts/BudgetStartDialog';
import { useAutomationGate } from '@/features/automation/useAutomationGate';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { rangeDates } from '@/features/envelope/envelopeRange';
import { useEnvelopeRange } from '@/features/envelope/useEnvelopeRange';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { ImportModal } from '@/features/imports/ImportModal';
import {
  booleanParam,
  intParam,
  optionalEnumParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import {
  ReviewPanel,
  ReviewTabLoading,
  ReviewTabs,
  type ReviewTabItem,
} from '@/components/ReviewTabs';
import { useReviewCount, useReviewTab } from '@/components/reviewTab';
import { identifierDisplay } from '@/features/accounts/identifierDisplay';
import { NotFoundBody, RecordNotFound } from '@/routes/NotFound';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { BankLedgerTile } from '@/features/connections/BankLedgerTile';
import { PendingStrip } from '@/features/connections/PendingStrip';
import { AccountFormDialog } from './AccountFormDialog';
import { OpeningBalanceRow } from './OpeningBalanceRow';
import { BalanceCheckCard, type RegisterDivergence } from './BalanceCheckCard';
import { CheckBalanceDialog } from './CheckBalanceDialog';
import { FxRatesDrawer } from './FxRatesDrawer';
import {
  canEditAllocations,
  canImportBatches,
  canViewFullRegister,
  isForeignCurrency,
  isOwnerAccount,
} from './accountAccess';
import { ImportsTab } from '@/features/imports/ImportsTab';
import { ReviewQueue } from '@/features/review/ReviewQueue';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { useSuggestMatches } from '@/features/review/useSuggestMatches';
import { CoverageDialog } from '@/features/planned/CoverageDialog';
import { ManualTransactionDialog } from './ManualTransactionDialog';
import { HeadedCard } from '@/components/HeadedCard';
import { TileMoney, V2Tile } from '@/components/V2Tile';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import { OwnedEnvelopesProvider } from './OwnedEnvelopesProvider';
import { useOwnedEnvelopes } from './useOwnedEnvelopes';
import { AccountForecastTab } from './ForecastTab';
import { InstitutionProposalLine } from './InstitutionProposal';
import { proposalOf, proposalReviewCount } from './useInstitutionAnswer';
import { OwnerRegisterTab, type RegisterFilter } from './OwnerRegisterTab';
import { RecurringTab } from './RecurringTab';
import { PermissionsTab } from './PermissionsTab';
import { RulesTab } from './rules/RulesTab';
import { ScheduleTab } from './ScheduleTab';
import { unmanagedCardBorderSx } from './unmanagedStyle';
import { formatCivilDate, formatInstant, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

type AccountState =
  | { status: 'loading' }
  | { status: 'ready'; account: Account }
  | { status: 'notFound' }
  | { status: 'error'; error: unknown };

type TabKey =
  | 'review'
  | 'register'
  | 'recurring'
  | 'cashflow'
  | 'rules'
  | 'permissions'
  | 'schedule'
  | 'imports';
/** The tabs after Items to review. */
type MainTabKey = Exclude<TabKey, 'review'>;
type DialogState =
  | {
      kind: 'edit';
      focusOpening?: boolean;
      fromBalanceFix?: boolean;
      suggestedOpeningMinor?: number;
    }
  | { kind: 'close' }
  | { kind: 'manual' }
  | { kind: 'subscribe' }
  | { kind: 'coverage' }
  | { kind: 'check' }
  | null;

const TAB_KEYS = [
  'review',
  'register',
  'recurring',
  'cashflow',
  'rules',
  'permissions',
  'schedule',
  'imports',
] as const;
const URL_SCHEMA = {
  // Absent: the default by the Items to review count (TMMAPP-0159 §F 27); a chosen tab is kept.
  tab: optionalEnumParam<TabKey>(TAB_KEYS),
  unallocated: booleanParam(),
  allocated: booleanParam(),
  envelope: intParam(0, { min: 0 }),
  /** Register search (TMMAPP-0133). Under two characters is kept but not sent. */
  q: stringParam(),
  /** A line to highlight, from search across accounts. 0 means none. */
  line: intParam(0),
};
const REVIEW_PANEL_ID = 'account-review';

function tabOf(raw: TabKey, account: Account, owner: boolean): TabKey {
  const unmanaged = account.management === 'UNMANAGED';
  if (raw === 'review' && !unmanaged) return 'review';
  if (raw === 'rules' && (owner || account.myPermissions.canEdit) && !unmanaged) return 'rules';
  if (raw === 'permissions' && owner && !unmanaged) return 'permissions';
  if (raw === 'schedule' && account.type === 'LOAN' && !unmanaged) return 'schedule';
  // Recurring and the Cashflow forecast are the cash account's tabs (TMMAPP-0102 §2); a revolving or
  // loan account keeps its existing Cashflow tab after the prototype's tabs (D1).
  if (raw === 'recurring' && account.type === 'CASH' && !unmanaged) return 'recurring';
  if (raw === 'cashflow' && !unmanaged) return 'cashflow';
  if (raw === 'imports' && (owner || account.myPermissions.canImport) && !unmanaged)
    return 'imports';
  return 'register';
}

/**
 * The chip row's meta line (§E 13, P:499): "{institution} ···· {last4} · Type · Subtype · CUR · last
 * import {date} · {profile}", whatever of it exists. The served identifier (IBAN, CLABE) wins over the
 * mask.
 */
function accountMeta(account: Account, locale: string, timeZone: string, t: TFunction): string {
  const served = identifierDisplay(account.externalIdentifier, account.externalIdentifierKind);
  const tail = served ?? (account.last4 ? `···· ${account.last4}` : '');
  const identity = [institutionLabel(account) ?? '', tail]
    .filter((bit) => bit.length > 0)
    .join(' ');
  const last = account.lastImport;
  return [
    identity,
    t(`accounts.type.${account.type}`),
    t(`accounts.subtype.${account.subtype}`),
    account.currency ?? '',
    last === null || last.at === null
      ? ''
      : t('accounts.metaLastImport', {
          date: formatAsOf(last.at, locale, timeZone, { month: 'short', day: 'numeric' }),
        }),
    last?.profileName ?? '',
  ]
    .filter((bit) => bit.length > 0)
    .join(' · ');
}

/** A served "as of": a civil date as received, an instant in the viewer's zone. */
function formatAsOf(
  value: string,
  locale: string,
  timeZone: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCivilDate(value, locale, options);
  try {
    return formatInstant(value, locale, timeZone, options);
  } catch {
    return value;
  }
}

function AccountDetailView({
  accountId,
  importOpen,
  importBatchId,
}: {
  accountId: number;
  importOpen: boolean;
  importBatchId: number | null;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const guest = me.tier === 'GUEST';
  const navigate = useNavigate();
  const tokens = resolveTokens(useTheme());
  const gate = useAutomationGate(!guest);
  const [
    {
      tab: requestedTab,
      unallocated: unallocatedParam,
      allocated: allocatedParam,
      envelope: envelopeParam,
      q: searchQuery,
      line: highlightLine,
    },
    setUrl,
  ] = useUrlState(URL_SCHEMA);
  // One filter in the URL; `unallocated` wins if a hand-edited link carries both.
  const registerFilter: RegisterFilter = unallocatedParam
    ? 'unallocated'
    : allocatedParam
      ? 'allocated'
      : 'all';
  const { envelopes: ownedEnvelopes, status: ownedEnvelopesStatus } = useOwnedEnvelopes();
  // A `envelope` that is not one of the owner's envelopes (deleted, moved, mistyped) is dropped
  // from the URL, by replace, once the list has loaded (TMMAPP-0097 B18).
  const staleEnvelope =
    envelopeParam > 0 &&
    ownedEnvelopesStatus === 'ready' &&
    !ownedEnvelopes.some((envelope) => envelope.id === envelopeParam);
  useEffect(() => {
    if (staleEnvelope) setUrl({ envelope: 0 });
  }, [staleEnvelope, setUrl]);
  const [state, setState] = useState<AccountState>({ status: 'loading' });
  const [registerRange, setRegisterRange] = useEnvelopeRange();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [fxOpen, setFxOpen] = useState(false);
  const notify = useNotify();
  const [openTxn, setOpenTxn] = useState<number | null>(null);
  const [injectedLine, setInjectedLine] = useState<OwnerRegisterLine | null>(null);
  const [registerTick, setRegisterTick] = useState(0);
  const [registerFocus, setRegisterFocus] = useState<RegisterDivergence | null>(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [confirmingOpening, setConfirmingOpening] = useState(false);
  const suggestMatches = useSuggestMatches({
    accountId,
    gate,
    onNotify: notify,
    onDone: () => setRegisterTick((n) => n + 1),
  });
  const started = useRef(false);
  const location = useLocation();
  // Items to review (TMMAPP-0159 §F 29): the count is the account's queue as it renders it; an
  // unmanaged account has no queue, so no review tab.
  const [queueCount, reportReviewCount] = useReviewCount();
  // A standing institution proposal is one more item there (TMMAPP-0163 item 12), counted with the
  // queue's served items, as the account's card counts it.
  const proposalCount = state.status === 'ready' ? proposalReviewCount(state.account) : 0;
  const reviewCount = typeof queueCount === 'number' ? queueCount + proposalCount : queueCount;
  const reviewEnabled = state.status === 'ready' && state.account.management !== 'UNMANAGED';
  // A link into the register's lines (a search hit, a line, an allocation filter) is a link to the
  // register: it wins over the default like a `?tab=` would.
  const registerLink =
    searchQuery !== '' || highlightLine > 0 || unallocatedParam || allocatedParam;
  const chosenTab = useReviewTab<MainTabKey>(
    requestedTab ?? (registerLink ? 'register' : null),
    reviewCount,
    'register',
    reviewEnabled,
  );
  // The old anchor (and any link to it) opens Items to review, by replace.
  const tidyUpAnchor = location.hash === '#tidy-up' && requestedTab === null;
  useEffect(() => {
    if (tidyUpAnchor) setUrl({ tab: 'review' });
  }, [tidyUpAnchor, setUrl]);

  const load = useCallback(async (): Promise<Account | null> => {
    try {
      const account = await accountsApi.get(accountId);
      setState({ status: 'ready', account });
      return account;
    } catch (error) {
      const next: AccountState =
        isApiError(error) && isNotFound(error)
          ? { status: 'notFound' }
          : { status: 'error', error };
      setState((prev) => (prev.status === 'ready' ? prev : next));
      return null;
    }
  }, [accountId, setState]);

  useEffect(() => {
    if (guest) return;
    if (started.current) return;
    started.current = true;
    void load();
  }, [guest, load]);

  const account = state.status === 'ready' ? state.account : null;
  const title = usePageTitle({
    title: guest
      ? t('nav.accounts')
      : (account?.name ??
        (state.status === 'notFound'
          ? t('notFound.title')
          : state.status === 'error'
            ? t('pageHeader.fallback.account')
            : null)),
    fallback: t('pageHeader.fallback.account'),
  });
  // "← My Accounts" (TMM-B7 1.4, P:761).
  const back =
    guest || state.status === 'notFound'
      ? undefined
      : { label: t('nav.accounts'), to: '/me/accounts' };

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error / not found.
  if (guest) {
    return <Navigate to="/me/accounts" replace />;
  }

  if (state.status === 'notFound') {
    return (
      <>
        <PageHeader title={title} />
        <NotFoundBody />
      </>
    );
  }

  if (state.status === 'loading') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Skeleton variant="rounded" height={120} />
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <ErrorState
          error={state.error}
          what={t('errors.what.account')}
          onRetry={() => {
            setState({ status: 'loading' });
            started.current = false;
            void load();
          }}
        />
      </>
    );
  }

  const row = state.account;
  const unmanaged = row.management === 'UNMANAGED';
  const subscriber = me.tier === 'SUBSCRIBER';
  const owner = isOwnerAccount(row);
  const canWrite = subscriber && owner && row.status === 'OPEN' && !unmanaged;
  const editor = subscriber && canEditAllocations(row) && row.status === 'OPEN' && !unmanaged;
  const showRegister = canViewFullRegister(row);
  const canDownload = canViewFullRegister(row) && !unmanaged;
  const institutionProposal = unmanaged ? null : proposalOf(row);
  const importer = canImportBatches(row) && row.status === 'OPEN' && !unmanaged;
  // `null` while the default waits for the review count (no flash).
  const tab = chosenTab === null ? null : tabOf(chosenTab, row, owner);
  const operating = row.operatingCurrency ?? me.user.currency ?? row.currency ?? '';
  const bank = row.currency ?? operating;
  const foreign = isForeignCurrency(row);
  // The register's window, which the downloads follow (TMMAPP-0115 A1); `null` while a custom range
  // is bad, and the downloads wait for it to be fixed.
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const registerDates = rangeDates(registerRange, today);
  async function handlePromote() {
    try {
      await accountsApi.update(row.id, { management: 'MANAGED' });
      notify(t('accounts.promote'));
      await load();
    } catch (error) {
      notify(errorCopy(t, error, 'accounts.errorGeneric'));
    }
  }

  async function handleIncludeInNetWorth(checked: boolean) {
    try {
      await accountsApi.update(row.id, { includeInNetWorth: checked });
      await load();
    } catch (error) {
      notify(errorCopy(t, error, 'accounts.errorGeneric'));
    }
  }

  function selectTab(next: TabKey) {
    setUrl(next === 'cashflow' ? { tab: next } : { tab: next, envelope: 0 }, { push: true });
  }

  function setRegisterFilter(next: RegisterFilter) {
    setUrl({ unallocated: next === 'unallocated', allocated: next === 'allocated' });
  }

  function changeRange(next: { from: string; to: string }) {
    setRegisterFocus(null);
    setRegisterRange(next);
  }

  function focusPageTitle() {
    requestAnimationFrame(() => document.getElementById('page-title')?.focus());
  }

  async function refreshAfterCheck() {
    setRegisterTick((n) => n + 1);
    return load();
  }

  // One card for one problem (TMMAPP-0134 item 7): an open check owns the opening question.
  const showOpeningCard =
    !unmanaged &&
    showRegister &&
    row.balanceCheck === null &&
    (row.openingPending || !openingIsConfirmed(row)) &&
    (row.openingPending || row.openingBalanceMinor !== null);

  async function handleLooksRight() {
    if (confirmingOpening) return;
    setConfirmingOpening(true);
    try {
      await accountsApi.confirmOpening(row.id);
      await load();
      focusPageTitle();
    } catch (error) {
      notify(errorCopy(t, error, 'accounts.errorGeneric'));
    } finally {
      setConfirmingOpening(false);
    }
  }

  async function handleAdjust(check: BalanceCheck) {
    setCheckBusy(true);
    try {
      await accountsApi.adjustBalanceCheck(row.id, check.id);
      await refreshAfterCheck();
      focusPageTitle();
    } catch (error) {
      // The check is dated on or before the opening date (TMMAPI-0198; TMMAPP-0168 item 1).
      const beforeOpening = lineBeforeOpening(error);
      if (beforeOpening !== null) {
        throw beforeOpening.openingOn !== null
          ? t('accounts.balanceCheck.adjustBeforeOpeningOn', {
              date: formatCivilDate(beforeOpening.openingOn, locale),
            })
          : t('accounts.balanceCheck.adjustBeforeOpening');
      }
      throw errorCopy(t, error, 'accounts.errorGeneric');
    } finally {
      setCheckBusy(false);
    }
  }

  async function handleDismiss(check: BalanceCheck) {
    setCheckBusy(true);
    try {
      await accountsApi.dismissBalanceCheck(row.id, check.id);
      await refreshAfterCheck();
      focusPageTitle();
    } catch (error) {
      notify(errorCopy(t, error, 'accounts.errorGeneric'));
    } finally {
      setCheckBusy(false);
    }
  }

  function handleFind(divergence: RegisterDivergence) {
    setRegisterFocus(divergence);
    setUrl({ tab: 'register', unallocated: false, allocated: false });
    setRegisterRange({ from: divergence.postedOn, to: divergence.postedOn });
  }

  // Closing the import returns to the tab it was opened from (the Imports tab carries `?tab=imports`).
  function closeImport(to: TabKey | null = requestedTab) {
    setRegisterTick((n) => n + 1);
    void load();
    void navigate(`/me/accounts/${row.id}${to === null ? '' : `?tab=${to}`}`, {
      replace: true,
    });
  }

  // A Review row's transaction: the modal closes and the register opens it (dialogs never stack).
  function openFromImport(transactionId: number) {
    setOpenTxn(transactionId);
    closeImport('register');
  }

  async function handleExport() {
    if (registerDates === null) return;
    try {
      const { blob, filename } = await accountsApi.exportXlsx(row.id, registerDates);
      downloadBlob(blob, filename ?? `${slug(row.name, 'account')}.xlsx`);
      notify(t('virtualAccount.downloadStarted'));
    } catch {
      notify(t('accounts.errorGeneric'));
    }
  }

  async function handleExportCsv() {
    if (registerDates === null) return;
    try {
      const { blob, filename } = await exportsApi.myRegisterCsv({
        accountId: row.id,
        ...registerDates,
      });
      downloadBlob(blob, filename ?? `${slug(row.name, 'account')}.csv`);
      notify(t('exports.ready'));
    } catch {
      notify(t('exports.failed'));
    }
  }

  async function handleClose() {
    if (closeBusy) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      await accountsApi.close(row.id);
      setDialog(null);
      notify(t('accounts.closedSnack'));
      await load();
    } catch (error) {
      setCloseError(
        t(
          isApiError(error) && error.code === ErrorCodes.ACCOUNT_CLOSED
            ? 'accounts.errorClosed'
            : 'accounts.errorGeneric',
        ),
      );
    } finally {
      setCloseBusy(false);
    }
  }

  function handleCreated(line: OwnerRegisterLine) {
    // The new line opens in the register, so its tab opens (from Items to review too).
    setUrl({ tab: 'register' });
    setInjectedLine(line);
    setOpenTxn(line.id);
    setRegisterTick((n) => n + 1);
  }

  const balances = row.balances;
  const timeZone = me.user.timezone ?? 'UTC';
  const asOf = balances?.asOf != null ? formatAsOf(balances.asOf, locale, timeZone) : null;

  // The tiles (§E 14, P:497): bank-side figures in the account's currency, allocations in the
  // operating currency (MM-D-63). A pending opening's figures are null on purpose and show "—".
  const bankSub = (note: string) =>
    asOf !== null ? t('accounts.tile.asOf', { note, date: asOf }) : note;
  const stats = (() => {
    if (row.type === 'CASH') {
      const operatingBalance = balances?.operatingBalanceMinor ?? null;
      return [
        <V2Tile
          key="bank"
          label={t('accounts.tile.bank')}
          value={<TileMoney minor={balances?.bankBalanceMinor ?? null} currency={bank} />}
          sub={bankSub(t('accounts.tile.bankSub'))}
        >
          {foreign && operatingBalance !== null && (
            <Typography
              component="span"
              data-tile-operating=""
              sx={{ fontSize: 12, lineHeight: '17px', color: tokens.textLabel('surface') }}
            >
              {t('accounts.tile.operating', {
                amount: formatMoneyMinor(operatingBalance, operating, locale),
              })}
            </Typography>
          )}
        </V2Tile>,
        <V2Tile
          key="allocated"
          label={t('accounts.tile.allocated')}
          value={<TileMoney minor={balances?.allocatedMinor ?? null} currency={operating} />}
          sub={t('accounts.tile.allocatedSub')}
        />,
        <V2Tile
          key="unallocated"
          label={t('accounts.tile.unallocated')}
          value={<TileMoney minor={balances?.unallocatedMinor ?? null} currency={operating} />}
          sub={t('accounts.tile.unallocatedSub')}
        />,
      ];
    }
    if (row.type === 'REVOLVING') {
      const published = balances?.allocatedMinor ?? null;
      const unpublished = balances?.unallocatedMinor ?? null;
      return [
        <V2Tile
          key="statement"
          label={t('accounts.tile.statement')}
          value={
            <TileMoney
              minor={balances?.statement == null ? null : 0 - Math.abs(balances.statement)}
              currency={bank}
            />
          }
          sub={bankSub(t('accounts.tile.bankSub'))}
        />,
        // As served (signed: a credit or refund remainder reads as one). A served null figure is left
        // out; a pending account's are null on purpose and show "—".
        ...(published === null && !row.openingPending
          ? []
          : [
              <V2Tile
                key="published"
                label={t('accounts.tile.published')}
                value={<TileMoney minor={published} currency={operating} />}
                sub={t('accounts.tile.publishedSub')}
              />,
            ]),
        ...(unpublished === null && !row.openingPending
          ? []
          : [
              <V2Tile
                key="unallocated"
                label={t('accounts.tile.unallocated')}
                value={<TileMoney minor={unpublished} currency={operating} />}
                sub={t('accounts.tile.unallocatedSub')}
              />,
            ]),
      ];
    }
    const fromRegister = balances?.balance != null;
    const current = row.openingPending
      ? null
      : fromRegister
        ? balances!.balance
        : (balances?.amortized ?? null);
    return [
      <V2Tile
        key="loan"
        label={t('accounts.colBalance')}
        value={
          <TileMoney minor={current === null ? null : 0 - Math.abs(current)} currency={bank} />
        }
        sub={
          row.openingPending
            ? undefined
            : fromRegister
              ? t('accounts.loanBalanceRegister')
              : t('accounts.loanBalanceAmortized')
        }
      />,
    ];
  })();

  // The tabs after Items to review, in their order (the old strip's conditions).
  const mainTabs: ReviewTabItem<MainTabKey>[] = [
    { value: 'register', label: t('accounts.registerTitle') },
    ...(row.type === 'CASH'
      ? [
          { value: 'recurring' as const, label: t('accounts.tabRecurring') },
          { value: 'cashflow' as const, label: t('accounts.tabCashflow') },
        ]
      : []),
    ...(row.type === 'LOAN'
      ? [{ value: 'schedule' as const, label: t('accounts.tabSchedule') }]
      : []),
    ...(row.type !== 'CASH'
      ? [{ value: 'cashflow' as const, label: t('accounts.tabCashflow') }]
      : []),
    ...(owner || row.myPermissions.canEdit
      ? [{ value: 'rules' as const, label: t('accounts.tabRules') }]
      : []),
    ...(owner ? [{ value: 'permissions' as const, label: t('accounts.tabPermissions') }] : []),
    ...(owner || row.myPermissions.canImport
      ? [{ value: 'imports' as const, label: t('accounts.tabImports') }]
      : []),
  ];

  const canSuggest = owner && !unmanaged;
  const hasMenu = canWrite || canDownload || importer || canSuggest;
  const menuButton = hasMenu ? (
    <OverflowMenu label={t('accounts.actions')} title={row.name} variant="page">
      {(close) => {
        const pick = (action: () => void) => () => {
          close();
          action();
        };
        return [
          canWrite && (
            <MenuItem key="edit" onClick={pick(() => setDialog({ kind: 'edit' }))}>
              {t('accounts.edit')}
            </MenuItem>
          ),
          canDownload && (
            <MenuItem
              key="transactions"
              disabled={registerDates === null}
              onClick={pick(() => void handleExportCsv())}
            >
              {t('accounts.downloadTransactions')}
            </MenuItem>
          ),
          canWrite && !showOpeningCard && (
            <MenuItem
              key="edit-opening"
              onClick={pick(() => setDialog({ kind: 'edit', focusOpening: true }))}
            >
              {t('accounts.editOpening')}
            </MenuItem>
          ),
          canWrite && (
            <MenuItem key="check-balance" onClick={pick(() => setDialog({ kind: 'check' }))}>
              {t('accounts.balanceCheck.checkMenu')}
            </MenuItem>
          ),
          canWrite && (
            <MenuItem key="manual" onClick={pick(() => setDialog({ kind: 'manual' }))}>
              {t('accounts.manualTransaction')}
            </MenuItem>
          ),
          importer && (
            <MenuItem
              key="import"
              disabled={gate.loading}
              onClick={pick(() => {
                if (gate.loading) return;
                if (gate.needsConsent) {
                  void navigate('/me/automation-help');
                  return;
                }
                void navigate(`/me/accounts/${row.id}/import`);
              })}
            >
              {gate.loading
                ? t('common.loading')
                : gate.needsConsent
                  ? t('consent.chooseFirst')
                  : t('accounts.import')}
            </MenuItem>
          ),
          canSuggest && (
            <MenuItem
              key="suggest"
              disabled={suggestMatches.disabled}
              onClick={pick(() => void suggestMatches.suggest())}
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              {t('review.suggestMatches')}
              {suggestMatches.reason !== null && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', whiteSpace: 'normal' }}
                >
                  {suggestMatches.reason}
                </Typography>
              )}
            </MenuItem>
          ),
          canWrite && (
            <MenuItem key="coverage" onClick={pick(() => setDialog({ kind: 'coverage' }))}>
              {t('accounts.setCoverage')}
            </MenuItem>
          ),
          canDownload && (
            <MenuItem
              key="workbook"
              disabled={registerDates === null}
              onClick={pick(() => void handleExport())}
            >
              {t('accounts.downloadWorkbook')}
            </MenuItem>
          ),
          canWrite && (
            <MenuItem
              key="close"
              onClick={pick(() => {
                setCloseError(null);
                setDialog({ kind: 'close' });
              })}
              sx={destructiveItemSx(tokens)}
            >
              {t('accounts.close')}
            </MenuItem>
          ),
        ];
      }}
    </OverflowMenu>
  ) : undefined;

  // Bare inline tags after the subtitle (visual contract §9): 10/500 uppercase text, no fill.
  const microTag = (key: string, label: string) => (
    <Typography key={key} variant="micro" component="span" sx={{ color: tokens.textLabel('bg') }}>
      {label}
    </Typography>
  );

  return (
    <>
      <PageHeader
        title={title}
        back={back}
        actions={
          <AskAboutThis
            name={t('askAbout.nameAbout', { name: row.name })}
            scope={[{ kind: 'ACCOUNT', id: row.id }]}
          />
        }
        detail={
          !unmanaged ? (
            <BudgetStartLine
              budgetStartOn={row.budgetStartOn}
              historicalLineCount={row.historicalLineCount ?? 0}
              balancedThroughOn={row.balancedThroughOn}
              canChange={canWrite}
              onSave={async (next) => {
                try {
                  await accountsApi.update(row.id, { budgetStartOn: next });
                  notify(t('budget.saved'));
                  await load();
                  setRegisterTick((n) => n + 1);
                } catch (error) {
                  notify(errorCopy(t, error, 'accounts.errorGeneric'));
                  throw error;
                }
              }}
            />
          ) : undefined
        }
        // No permission pill under the title (TMMAPP-0161 item 3); the account cards keep theirs.
        status={
          <>
            <Typography
              component="span"
              data-account-meta=""
              sx={{ fontSize: 13, lineHeight: '20px', color: tokens.textLabel('bg') }}
            >
              {accountMeta(row, locale, timeZone, t)}
            </Typography>
            {unmanaged && microTag('unmanaged', t('accounts.unmanagedChip'))}
            {row.status === 'CLOSED' && microTag('closed', statusLabel(t, 'account', row.status))}
            {!owner && microTag('granted', t('accounts.grantedByOwner'))}
            {row.connector !== null &&
              microTag(
                'connector',
                [
                  row.connector.institutionName,
                  row.connector.mask !== null && row.connector.mask.length > 0
                    ? t('connections.mask', { mask: row.connector.mask })
                    : null,
                ]
                  .filter((bit): bit is string => bit !== null && bit.length > 0)
                  .join(' · '),
              )}
          </>
        }
        menu={menuButton}
      />

      {foreign && (
        <Stack
          direction="row"
          data-fx-banner=""
          sx={{
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            bgcolor: tokens.surface,
            backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
            borderRadius: `${tokens.radius.control}px`,
            p: '12px 16px',
            fontSize: 13,
          }}
        >
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
            {t('accounts.fxBadge')}
          </Typography>
          <Typography component="span" sx={{ fontSize: 13, flex: '1 1 auto' }}>
            {t('accounts.fxNote', { currency: bank, operating })}
          </Typography>
          {owner && showRegister && (
            <Button variant="text" size="small" onClick={() => setFxOpen(true)}>
              {t('fxRates.open')}
            </Button>
          )}
        </Stack>
      )}

      <Box data-account-stats="" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {stats}
      </Box>

      {unmanaged && (
        <HeadedCard
          title={t('accounts.unmanagedBlockTitle')}
          subtitle={t('accounts.unmanagedBlockSub')}
          sx={unmanagedCardBorderSx(tokens)}
        >
          <Stack spacing={1}>
            {owner && subscriber && (
              <ConfirmButton
                size="small"
                variant="outlined"
                onConfirm={() => void handlePromote()}
                aria-description={t('accounts.promoteConfirm')}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('accounts.promote')}
              </ConfirmButton>
            )}
            {owner && (
              <FormControlLabel
                control={
                  <Switch
                    checked={row.includeInNetWorth}
                    onChange={(_e, checked) => void handleIncludeInNetWorth(checked)}
                    disabled={!subscriber}
                  />
                }
                label={t('accounts.includeInNetWorth')}
              />
            )}
          </Stack>
        </HeadedCard>
      )}

      {showOpeningCard && (
        <HeadedCard title={t('accounts.openingTitle')}>
          <OpeningBalanceRow
            account={row}
            currency={bank}
            confirming={confirmingOpening}
            onConfirm={
              canWrite && !row.openingPending && !openingIsConfirmed(row)
                ? () => void handleLooksRight()
                : undefined
            }
            onEdit={canWrite ? () => setDialog({ kind: 'edit', focusOpening: true }) : undefined}
          />
        </HeadedCard>
      )}

      {!unmanaged && showRegister && row.balanceCheck !== null && (
        <BalanceCheckCard
          check={row.balanceCheck}
          currency={bank}
          busy={checkBusy}
          onFixOpening={
            canWrite && row.balanceCheck.constantFromStart
              ? () =>
                  setDialog({
                    kind: 'edit',
                    focusOpening: true,
                    fromBalanceFix: true,
                    ...(row.openingBalanceMinor !== null
                      ? {
                          suggestedOpeningMinor:
                            row.openingBalanceMinor + row.balanceCheck!.differenceMinor,
                        }
                      : {}),
                  })
              : undefined
          }
          onFind={handleFind}
          onAdjust={canWrite ? () => handleAdjust(row.balanceCheck!) : undefined}
          onDismiss={canWrite ? () => handleDismiss(row.balanceCheck!) : undefined}
        />
      )}

      {owner && showRegister && !unmanaged && <BankLedgerTile account={row} />}

      {suggestMatches.walletRefusal !== null && (
        <AiWalletPrompt
          refusal={suggestMatches.walletRefusal}
          onTryAgain={() => void suggestMatches.suggest()}
        />
      )}

      {/* The tabs (TMMAPP-0159 §F 29): Items to review, then Bank register, then the others. An
          unmanaged account has no queue and no other tabs: its register shows on its own. */}
      {!unmanaged && (
        <ReviewTabs
          value={tab}
          count={reviewCount}
          tabs={mainTabs}
          onChange={selectTab}
          label={t('accounts.tabsLabel')}
          panelId={REVIEW_PANEL_ID}
          sx={{ mb: 2 }}
        />
      )}
      {tab === null && <ReviewTabLoading />}
      {!unmanaged && (
        <ReviewPanel
          id={REVIEW_PANEL_ID}
          active={tab === 'review'}
          count={reviewCount}
          mainLabel={t('accounts.registerTitle')}
          onMain={() => selectTab('register')}
        >
          {institutionProposal !== null && (
            <InstitutionProposalLine
              account={row}
              proposal={institutionProposal}
              onNotify={notify}
              onAnswered={() => void load()}
            />
          )}
          <ReviewQueue
            accountId={row.id}
            onNotify={notify}
            reloadToken={registerTick}
            onCount={reportReviewCount}
          />
        </ReviewPanel>
      )}

      {(unmanaged || tab === 'register') &&
        (showRegister ? (
          <>
            {unmanaged && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {t('accounts.unmanagedRegisterNote')}
              </Typography>
            )}
            {!unmanaged && <PendingStrip account={row} />}
            <OwnerRegisterTab
              account={row}
              editor={editor}
              owner={canWrite}
              registerFilter={registerFilter}
              onRegisterFilter={setRegisterFilter}
              range={registerRange}
              onRangeChange={changeRange}
              registerFocus={registerFocus}
              today={today}
              openTransactionId={openTxn}
              onOpenTransaction={setOpenTxn}
              injectedLine={injectedLine}
              reloadToken={registerTick}
              onNotify={notify}
              canImport={importer}
              onRegisterMutated={() => void load()}
              searchQuery={searchQuery}
              onSearchQuery={(value) => setUrl({ q: value, line: 0 })}
              highlightLineId={highlightLine > 0 ? highlightLine : null}
            />
          </>
        ) : (
          <Typography variant="body1">{t('accounts.registerHidden')}</Typography>
        ))}

      {tab === 'rules' && (owner || row.myPermissions.canEdit) && (
        <RulesTab
          account={row}
          writable={canWrite}
          lapsed={owner && me.tier === 'NONE'}
          readOnlyNote={!owner}
          operatingCurrency={operating}
          onNotify={notify}
          onSubscriptionRequired={() => setDialog({ kind: 'subscribe' })}
        />
      )}

      {tab === 'permissions' && owner && (
        <PermissionsTab
          accountId={row.id}
          readOnly={!canWrite}
          onNotify={notify}
          onSubscriptionRequired={() => setDialog({ kind: 'subscribe' })}
        />
      )}

      {tab === 'schedule' && row.type === 'LOAN' && (
        <ScheduleTab key={registerTick} accountId={row.id} currency={bank} />
      )}

      {tab === 'imports' && (owner || row.myPermissions.canImport) && (
        <ImportsTab accountId={row.id} canStart={importer} />
      )}

      {tab === 'recurring' && row.type === 'CASH' && (
        <RecurringTab
          account={row}
          operating={operating}
          canWrite={canWrite}
          reloadToken={registerTick}
          onNotify={notify}
        />
      )}

      {tab === 'cashflow' && (
        <AccountForecastTab
          account={row}
          operating={operating}
          bank={bank}
          owner={owner}
          envelopeId={envelopeParam > 0 && !staleEnvelope ? envelopeParam : 0}
          onEnvelopeChange={(id: number) => setUrl({ tab: 'cashflow', envelope: id })}
          reloadToken={registerTick}
          onNotify={notify}
        />
      )}

      {dialog?.kind === 'edit' && (
        <AccountFormDialog
          operatingCurrency={operating}
          account={row}
          focusOpening={dialog.focusOpening === true}
          {...(dialog.suggestedOpeningMinor !== undefined
            ? { suggestedOpeningMinor: dialog.suggestedOpeningMinor }
            : {})}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            const fixing = dialog.fromBalanceFix === true;
            setRegisterTick((n) => n + 1);
            const next = await load();
            if (fixing && next?.balanceCheck == null) notify(t('accounts.balanceCheck.fixed'));
          }}
          // A switch to managed offers the import, through the same consent gate (TMMAPP-0139).
          onImport={(account) => {
            setDialog(null);
            void navigate(
              gate.needsConsent ? '/me/automation-help' : `/me/accounts/${account.id}/import`,
            );
          }}
        />
      )}
      {dialog?.kind === 'check' && (
        <CheckBalanceDialog
          account={row}
          today={today}
          onClose={() => setDialog(null)}
          onRecorded={(check) => {
            setDialog(null);
            void refreshAfterCheck().then(() => {
              if (check.status === 'BALANCED' || check.differenceMinor === 0) {
                notify(
                  t('accounts.balanceCheck.balancedThrough', {
                    date: formatCivilDate(check.on, locale),
                  }),
                );
              }
            });
          }}
        />
      )}
      {dialog?.kind === 'manual' && (
        <ManualTransactionDialog
          account={row}
          onClose={() => setDialog(null)}
          onCreated={handleCreated}
          onSubscriptionRequired={() => setDialog({ kind: 'subscribe' })}
        />
      )}
      {dialog?.kind === 'coverage' && (
        <CoverageDialog
          account={row}
          onClose={() => setDialog(null)}
          onSaved={(message) => {
            notify(message);
            void load();
          }}
          onSubscriptionRequired={() => setDialog({ kind: 'subscribe' })}
        />
      )}
      {dialog?.kind === 'close' && (
        <ConfirmDialog
          title={t('accounts.closeTitle', { name: row.name })}
          consequence={t('accounts.closeBody')}
          confirmLabel={t('accounts.close')}
          destructive
          busy={closeBusy}
          error={closeError}
          onConfirm={() => void handleClose()}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'subscribe' && (
        <Dialog
          open
          onClose={() => setDialog(null)}
          maxWidth="xs"
          aria-label={t('subscribe.cardTitle')}
        >
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
      {importOpen && (
        <ImportModal
          accountId={row.id}
          batchId={importBatchId}
          onClose={() => closeImport()}
          onOpenTransaction={openFromImport}
          // Confirm's "Change opening date": the modal closes and the starting balance editor
          // opens (dialogs never stack; TMMAPP-0139 item 8).
          onChangeOpening={
            canWrite
              ? () => {
                  closeImport();
                  setDialog({ kind: 'edit', focusOpening: true });
                }
              : undefined
          }
        />
      )}
      {fxOpen && owner && foreign && (
        <FxRatesDrawer base={bank} quote={operating} onClose={() => setFxOpen(false)} />
      )}
    </>
  );
}

export function AccountPage() {
  const params = useParams();
  const accountId = parseRouteId(params.accountId);
  // `/me/accounts/:id/import[/:batchId]` is this page with the import modal open over it (D4).
  const importMatch = useMatch({ path: '/me/accounts/:accountId/import', end: false });
  if (accountId === null) return <RecordNotFound />;
  return (
    <OwnedEnvelopesProvider>
      <AccountDetailView
        key={accountId}
        accountId={accountId}
        importOpen={importMatch !== null}
        importBatchId={parseRouteId(params.batchId)}
      />
    </OwnedEnvelopesProvider>
  );
}
